import * as React from 'react';
import type { PropsWithChildren } from 'react';
import type { CmdbObject, CmdbObjectAttribute } from 'rest/cmdb-object-picker';
import type { OptionProps } from '@atlaskit/select';
import { components } from '@atlaskit/select';
import { MemoizedInsightIcon } from '../insight-icon';
import { Content, Icon, Outer, PrimaryAttribute, SecondaryAttributes } from './styled';

export const BoldMatchingText: React.FC<{ match: string; children: string }> = ({ match, children }) => {
    if (match === '') {
        return <>{children}</>;
    }

    // we need to escape special characters to avoid getting them interpreted in the RegExp group
    const escapedSpecialCharsInMatchQuery = match.replace(/[\\.,*+?^${}()|[\]]/g, '\\$&');
    const parts = children.split(new RegExp(`(${escapedSpecialCharsInMatchQuery})`, 'gi'));
    return (
        <>
            {parts.map((part, i) => {
                if (part.toLowerCase() === match.toLowerCase()) {
                    return <strong key={i}>{part}</strong>;
                }
                return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
        </>
    );
};

export const getVisibleAttributes = (
    attributes: CmdbObjectAttribute[],
    attributesToDisplay: string[]
): CmdbObjectAttribute[] => {
    const notUndefined = (attr: CmdbObjectAttribute | undefined): attr is CmdbObjectAttribute => {
        return Boolean(attr);
    };
    return (
        attributesToDisplay
            .map((attrName) =>
                attrName === 'Label'
                    ? attributes.find((attr) => attr.objectTypeAttribute.label)
                    : attributes.find((attr) => attr.objectTypeAttribute.name === attrName)
            )
            // get only attributes that have valid displayValue
            .filter(notUndefined)
            .filter(
                (attribute) =>
                    attribute.objectAttributeValues.length > 0 &&
                    attribute.objectAttributeValues.every((value) => value && value.displayValue)
            )
    );
};

export const createAttributeString = (attributes: CmdbObjectAttribute[]): string => {
    return attributes
        .map((attribute) => attribute.objectAttributeValues.map((value) => value.displayValue).join(', '))
        .join(' | ');
};

export const createLabelString = (attributes: CmdbObjectAttribute[], defaultLabel: string): string => {
    return attributes[0]?.objectAttributeValues.map((v) => v.displayValue).join(', ') || defaultLabel;
};

export type Props = OptionProps<string>;

export const CmdbSelectOption = (optionProps: PropsWithChildren<Props>) => {
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { data, innerProps, selectProps } = optionProps;
    const { inputValue, filterAttributes } = selectProps;
    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const object = data.cmdbObject as CmdbObject;
    const visibleAttributes = getVisibleAttributes(object.attributes, filterAttributes ?? []);
    const [, ...secondaryAttributes] = visibleAttributes;
    const labelAttribute = createLabelString(visibleAttributes, object.label);
    const secondaryAttributesDescription = createAttributeString(secondaryAttributes);
    const { Option } = components;

    return (
        <Option {...optionProps}>
            <Outer {...innerProps}>
                <Icon>
                    <MemoizedInsightIcon {...object.avatar.mediaClientConfig} />
                </Icon>
                <Content>
                    <PrimaryAttribute>
                        <BoldMatchingText match={inputValue ?? ''}>{labelAttribute}</BoldMatchingText>
                    </PrimaryAttribute>
                    <SecondaryAttributes>
                        <BoldMatchingText match={inputValue ?? ''}>{secondaryAttributesDescription}</BoldMatchingText>
                    </SecondaryAttributes>
                </Content>
            </Outer>
        </Option>
    );
};
